import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useExperimentSelector } from '../../../../store/selectors/experimentSelector'
import P from '../../../../elements/Typo/P'
import Text from '../../../../elements/Typo/Text'
import Button from '../../../../elements/button'
import { useExperimentApi } from '../../../../store/api/hooks/experimentApiHook'
import toast from 'react-hot-toast'
import Loader from '../../../../components/loader'
import Icon from '../../../../components/icons'
import { trackEvent } from '../../../../_common/functions'
import { URLS } from '../../../../_config'
import ModalCloseIconButton from '../../../../components/modalCloseIconButton'
import { EXPERIMENT_TYPES } from '../../../../_common/constant'

interface MeasurementSuccessModalProps {
    _ref: any
    mdata: any
    onMeasurement?: () => void
    updateExperiment?: any
}

const MeasurementSuccessModal: React.FC<MeasurementSuccessModalProps> = ({ _ref, mdata, onMeasurement = () => {}, updateExperiment = null }) => {
    const { t } = useTranslation()
    const experimentApi = useExperimentApi()
    const experiment = useExperimentSelector()
    const params = useParams()
    const navigate = useNavigate()
    const [working, setWorking] = useState<boolean>(false)
    const [matchpercentage, setMatchpercentage] = useState<boolean>(false)

    const submitExperiment = () => {
        const paramsData: any = {
            step: 6,
            is_draft: false,
            literal: params.experiment,
        }

        experimentApi.updateExperiment(
            paramsData,
            (data: any) => {
                console.log('Submited step2', data)
                updateExperiment?.current?.close()
                void navigate(URLS.MY_EXPERIMENTS)
            },
            (error: any) => {
                toast.error(error?.message)
            },
        )
    }

    const startMeasuring = () => {
        setWorking(true)
        const payload: any = {
            literal: params.experiment,
            step: 6,
            is_draft: false,
            is_partial_measurement: location.pathname.includes('/create-experiment/'),
        }
        experimentApi.updateExperiment(
            payload,
            (resp: any) => {
                trackEvent('measurement_data_added', {
                    experiment: params.experiment,
                })
                experimentApi.executemeasurmentanalysis(
                    { literal: params.experiment },
                    (resp: any) => {
                        if (resp.data?.task_id) {
                            sessionStorage.setItem('M_START', params.experiment ?? '')
                        }
                        setWorking(false)
                        _ref.current.close()
                        toast.success(t('Measurement Data Successfully Added.'))
                        if (updateExperiment) {
                            submitExperiment()
                        }

                        onMeasurement()
                    },
                    (error: any) => {
                        setWorking(false)
                        _ref.current.close()
                        toast.error(error?.message)
                    },
                )
            },
            (error: any) => {
                setWorking(false)
                toast.error(error?.message)
            },
        )
    }

    useEffect(() => {
        if (experiment?.experiment_type?.title == EXPERIMENT_TYPES.RCT.title || experiment?.experiment_type?.title == EXPERIMENT_TYPES.OBS.title) {
            if (Number(mdata?.data.matching_percentage) <= 49.99) {
                setMatchpercentage(true)
            } else {
                setMatchpercentage(false)
            }
        } else if (experiment?.experiment_type?.title == EXPERIMENT_TYPES.MTE.title) {
            if (Number(mdata?.data.match_percentage) <= 69.99) {
                setMatchpercentage(true)
            } else {
                setMatchpercentage(false)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mdata])

    const getMatchPercentageError = (): string => {
        if (experiment?.experiment_type?.title == EXPERIMENT_TYPES.MTE.title) {
            return t('Your data does not match 70%')
        } else {
            return t('Your data does not match 50%')
        }
    }

    return (
        <dialog ref={_ref} className="modal">
            <div className="modal-box" data-testid="measurement-success-modal">
                <ModalCloseIconButton onClick={() => _ref.current.close()} />

                {experiment?.experiment_type?.title == EXPERIMENT_TYPES.RCT.title || experiment?.experiment_type?.title == EXPERIMENT_TYPES.OBS.title ? (
                    <>
                        <P className="text-center font-semibold">{t('Comparing Design Data with Measurement Data')}</P>
                        <Text size="md" className="text-center rounded-[0.333rem] !leading-[1.625rem] bg-[#28A52680] font-semibold">
                            {mdata?.data.matching_percentage}% {t('Data overlapped with Design')}
                        </Text>

                        <div className="my-2 px-2 border border-[#f4f4f4] rounded-[0.333rem] flex items-center justify-between gap-2 h-[2.708rem]">
                            <Text size="lg" className="font-bold shrink-0">
                                {t('Design Data')}
                            </Text>
                            <Text size="15" className="!leading-none py-0.5 flex items-center px-2 rounded-[1.667rem] bg-[#EFEFEF]">
                                {mdata?.data?.design_data_count} {t('Unique')} {experiment?.experiment_metadatas?.measurement_column_map?.granularity} IDs
                            </Text>
                        </div>

                        <div className="my-2 px-2 border border-[#f4f4f4] rounded-[0.333rem] flex items-center justify-between gap-2 h-[2.708rem]">
                            <Text size="lg" className="font-bold shrink-0">
                                {t('KPI/Measurement Data')}
                            </Text>
                            <Text size="15" className="!leading-none py-0.5 flex items-center px-2 rounded-[1.667rem] bg-[#EFEFEF]">
                                {mdata?.data?.measurement_data_count} {t('Unique')} {experiment?.experiment_metadatas?.measurement_column_map?.granularity} IDs
                            </Text>
                        </div>
                        <div className="my-2 px-2 border border-[#E1960A33] bg-[#DAE10A80] rounded-[0.333rem] flex items-center justify-between gap-2 h-[2.708rem]">
                            <Text size="lg" className="font-bold shrink-0">
                                {t('Common from Both Data')}
                            </Text>
                            <Text size="15" className="!leading-none py-0.5 flex items-center px-2 rounded-[1.667rem] bg-[#FFFFFF]">
                                {mdata?.data?.matching_count} {t('Unique')} {experiment?.experiment_metadatas?.measurement_column_map?.granularity} IDs
                            </Text>
                        </div>
                    </>
                ) : null}

                {experiment?.experiment_type?.title == EXPERIMENT_TYPES.MTE.title ? (
                    <>
                        <P className="text-center font-semibold">{t('Comparing Intervention Data with Measurement Data')}</P>
                        <Text size="md" className="text-center rounded-[0.333rem] !leading-[1.625rem] bg-[#28A52680] font-semibold">
                            {mdata?.data.match_percentage}% {t('Data matched with Design')}
                        </Text>
                        <div className="my-2 px-2 border border-[#f4f4f4] rounded-[0.333rem] flex items-center justify-between h-[2.708rem]">
                            <Text size="lg" className="font-bold">
                                {t('Intervention Data')}
                            </Text>
                            <Text size="md" className="!leading-none h-[1rem] flex items-center px-2 rounded-[1.667rem] bg-[#EFEFEF]">
                                {mdata?.data?.intervention_count}
                            </Text>
                        </div>
                        <div className="my-2 px-2 border border-[#f4f4f4] rounded-[0.333rem] flex items-center justify-between gap-2 h-[2.708rem]">
                            <Text size="lg" className="font-bold shrink-0">
                                {t('Measurement Data')}
                            </Text>
                            <Text size="15" className="!leading-none py-0.5 flex items-center px-2 rounded-[1.667rem] bg-[#EFEFEF]">
                                {mdata?.data?.measurement_count} {t('Unique')} {experiment?.experiment_metadatas?.measurement_column_map?.granularity} IDs
                            </Text>
                        </div>
                        <div className="my-2 px-2 border border-[#E1960A33] bg-[#DAE10A80] rounded-[0.333rem] flex items-center justify-between gap-2 h-[2.708rem]">
                            <Text size="lg" className="font-bold shrink-0">
                                {t('Common from Both Data')}
                            </Text>
                            <Text size="15" className="!leading-none py-0.5 flex items-center px-2 rounded-[1.667rem] bg-[#FFFFFF]">
                                {mdata?.data?.match_count} {t('Unique')} {experiment?.experiment_metadatas?.measurement_column_map?.granularity} IDs
                            </Text>
                        </div>
                    </>
                ) : null}

                {mdata?.data?.is_group_names_matched !== undefined && !mdata?.data?.is_group_names_matched && (
                    <div className="bg-red-300 border items-center justify-center border-red-500 text-[0.667rem] text-black font-semibold rounded-[0.333rem] p-1 py-2 flex gap-2 mt-4">
                        <div>
                            <Icon name="warning" className="text-red-600" />
                        </div>
                        <div>{t('Group config names mismatched')}</div>
                    </div>
                )}

                <div className="flex justify-center mt-4">
                    {(matchpercentage && experiment?.experiment_type?.title !== EXPERIMENT_TYPES.MTE.title) || (mdata?.data.is_group_names_matched !== undefined && !mdata?.data.is_group_names_matched) ? (
                        <Button theme="secondary" size="sm" disabled={true} className="!text-red-900">
                            {matchpercentage ? t(getMatchPercentageError()) : t('Measure Experiment')}
                        </Button>
                    ) : (
                        <Button theme="secondary" size="sm" onClick={startMeasuring} disabled={working}>
                            {working ? <Loader /> : t('Measure Experiment')}
                        </Button>
                    )}
                </div>
            </div>
            <form method="dialog" className="modal-backdrop">
                <button type="button" data-testid="close-button" onClick={() => _ref.current.close()}>
                    {t('Close')}
                </button>
            </form>
        </dialog>
    )
}

export default MeasurementSuccessModal
